<template>
    <search-layout
        v-if="loaded"
        :map-expanded="mapExpanded"
        :offers-count="filteredOffers.length"
        :search-active="searchActive"
    >
        <template v-slot:breadcrumbs>
            <expired-offers-snackbar :is-offers-expired="isOffersExpired" @refresh="search" />
            <simple-tour-breadcrumbs :search-request="searchRequest" />
        </template>
        <template v-slot:left-sidebar>
            <simple-tour-search-summary :search-request="searchRequest" show-dates />
            <sort-select v-model="sortKey" :items="sortItems" />
            <group-offers-select
                class="pt-3"
                :items="['roomTypeName', 'mealTypeName', 'date', 'none']"
                :product-store="productStore"
            />
            <simple-tour-filters
                :filter-values="filterValues"
                :filters="searchResponse.filters"
                :filtered-offers-count="filteredOffers.length"
                :currency="currency"
                @change="changeFilter"
                @reset="resetFilters"
            />
        </template>
        <template v-slot:main-content>
            <search-progress
                :filtered-offers-count="filteredOffers?.length"
                :total-offers-count="searchResponse.products?.length"
                :search-active="searchActive"
                @stop="stopSearch"
            />
            <simple-tour-offers :offers="offers" />
            <pagination v-model="page" :pages="pages" />
            <conditions-modal>
                <template v-slot:info>
                    <activity-conditions-modal-info />
                </template>
            </conditions-modal>
            <simple-tour-offer-info-modal />
        </template>
        <template v-slot:right-sidebar>
            <client-only>
                <activity-map v-model="mapExpanded" :offers="[]" :city="city" />
            </client-only>
        </template>
    </search-layout>
</template>

<script lang="ts">
    import {Component, mixins, Watch} from 'vue-facing-decorator'
    import {SearchPageBase} from '@/mixins/SearchPageBase.vue'
    import {SimpleTourMixin} from '@/mixins/SimpleTourMixin.vue'
    import _Tmp_SearchPageBase from '@/mixins/Tmp_SearchPageBase.vue'
    import {SimpleTourPageMixin} from '@/components/simpleTours/mixins/SimpleTourPageMixin.vue'
    import {SearchPageMixin} from '@/components/mixins/SearchPageMixin.vue'
    import Pagination from '~/components/search/Pagination.vue'
    import ConditionsModal from '~/components/modals/ConditionsModal.vue'
    import ActivityMap from '@/components/activities/search/ActivityMap.vue'
    import SimpleTourOfferInfoModal from '@/components/simpleTours/info/SimpleTourOfferInfoModal.vue'
    import ExpiredOffersSnackbar from '@/components/search/ExpiredOffersSnackbar.vue'
    import SearchProgress from '~src/components/search/searchProgress.src'
    import SortSelect from '@/components/search/SortSelect.vue'
    import ActivityConditionsModalInfo from '@/components/activities/snippets/ActivityConditionsModalInfo.vue'
    import GroupOffersSelect from '@/components/search/GroupOffersSelect.vue'
    import SearchLayout from '~src/components/parts/searchLayout.src'
    import {useSimpleTourRuntimeData} from '~/composables/sipmle-tour'
    import {callComponentComputed} from '~/utils/vue-class-helpers'
    import SimpleTourBreadcrumbs from '~/components/simpleTours/SimpleTourBreadcrumbs.vue'
    import SimpleTourFilters from '~/components/simpleTours/search/SimpleTourFilters.vue'
    import SimpleTourSearchSummary from '@/components/simpleTours/search/SimpleTourSearchSummary.vue'
    import {simpleTourStore} from '~/composables/store-accessor'
    import SimpleTourOffers from '@/components/simpleTours/search/offers/SimpleTourOffers.vue'
    import type {RouteLocationNormalized} from 'vue-router'

    @Component({
        components: {
            SimpleTourOffers,
            SimpleTourBreadcrumbs,
            SimpleTourFilters,
            SimpleTourSearchSummary,
            ActivityConditionsModalInfo,
            SortSelect,
            SearchProgress,
            ExpiredOffersSnackbar,
            Pagination,
            ActivityMap,
            SimpleTourOfferInfoModal,
            ConditionsModal,
            SearchLayout,
            GroupOffersSelect,
        },
        async setup() {
            const route = useRoute()
            await useAsyncData(`simple-tour-${route.query.cityId}`, async () => {
                await useSimpleTourRuntimeData(route)
                return true
            })
        },
    })
    export class ActivitiesPage extends mixins(SimpleTourMixin, SimpleTourPageMixin, SearchPageBase, SearchPageMixin) {
        loaded = false

        @Watch('$route')
        onQueryChange(route: RouteLocationNormalized) {
            if (!route.path.includes('search')) return
            this.search()
        }

        async fetch() {
            await this.loadRuntimeData()
        }

        async mounted() {
            await this.$store.restored
            this.loaded = true
            if (
                !isEqual(simpleTourStore.searchRequest, this.searchRequest) ||
                this.isOffersExpired() ||
                !simpleTourStore.hasOffers
            ) {
                await this.search()
            } else if (simpleTourRuntimeStore.offers?.length === 0) {
                const offers = await this.$localForage.getItem('simpleTour')
                postMessageLoad('simpleTour', simpleTourStore, offers)
            }
            if (simpleTourStore.searchRequest?.cityId) {
                await simpleTourRuntimeStore.loadCity(simpleTourStore.searchRequest.cityId)
            }
        }

        async search() {
            await simpleTourRuntimeStore.search(this.searchRequest)
        }

        get city() {
            return this.productRuntimeStore.city
        }

        get sortItems() {
            return [
                ...callComponentComputed(this, _Tmp_SearchPageBase, 'sortItems'),
                {
                    text: this.$t('sort.date_asc'),
                    value: 'dateAsc',
                },
                {
                    text: this.$t('sort.name'),
                    value: 'name',
                },
            ]
        }

        get filteredOffers() {
            return simpleTourRuntimeStore.offers
        }

        get searchActive() {
            return simpleTourRuntimeStore.searchActive
        }

        stopSearch() {
            simpleTourRuntimeStore.stopSearch()
        }

        changeFilter(data) {
            simpleTourStore.SET_FILTER(data)
            simpleTourRuntimeStore.filter()
        }

        resetFilters() {
            simpleTourStore.RESET_FILTERS()
            simpleTourRuntimeStore.filter()
        }

        get filterValues() {
            return simpleTourStore.filters
        }

        get searchResponse() {
            return simpleTourStore.searchResponse
        }

        get sortKey() {
            return simpleTourStore.sortFnName
        }

        get currency() {
            return simpleTourStore.searchRequest.convertToCurrency
        }

        set sortKey(sortKey) {
            simpleTourStore.SET_SORT_FN_NAME(sortKey)
            simpleTourRuntimeStore.sort()
        }
    }

    export default ActivitiesPage
</script>
